/*
 * ════════════════════════════════════════════════════════════════════════════
 * ZEUS BOARD-FINDER QUIZ ENGINE
 * Custom element <zeus-quiz-engine> — native replacement for the Lantern PPF.
 *
 * SHORT flow (Lantern data: 96.6% abandoned the 13-Q app). Panels:
 *   welcome → 4 questions (level / conditions* / weight / height) → email →
 *   loading → results. (*conditions is optional.) Budget is gone.
 *
 * RESULTS = exactly 1 DEFAULT board ("Mon choix") + 1 ALTERNATIVE
 * ("Si tu hésites"), each with an honest explanation line. Never 3.
 *
 * Storage-agnostic: reads ONLY the DOM contract below. No hardcoded copy —
 * every string comes from data-t-* (sourced from locale files in the section).
 *
 * Preserved from v1: i18n via data-attrs · Klaviyo per-locale subscribe + Track
 * event · analytics sendBeacon funnel (Apps Script / Sheet) · email gate.
 *
 * ── RECO ALGORITHM (skill zeus-recommendation-engine) ───────────────────────
 *   level → base Guild Factor (GF_BASE); unknown level → 0.9.
 *   gf = clamp(round2(gfBase + conditionsAdj), 0.55, 1.1).
 *   targetVol = round(weight * gf)   ← safety bias lives in the asymmetric band below.
 *   DEFAULT = closest candidate in band [targetVol-6, targetVol+14] (tolerate
 *     MORE volume, never strand below safe minimum); tie → lengthBias sign,
 *     then lower price, then handle. Empty band → globally closest by abs.
 *   ALTERNATIVE = deliberately different: nearest candidate ABOVE default by
 *     +1..+14L (more_volume); else nearest BELOW by -1..-12L (less_volume);
 *     else next-closest distinct by abs. Weight ALWAYS primes over height.
 *
 * ── DOM CONTRACT ────────────────────────────────────────────────────────────
 * <zeus-quiz-engine
 *    data-company-id  data-list-id  data-locale  data-email-required
 *    data-question-total  data-discount-code  data-discount-url
 *    data-analytics-url  data-klaviyo-revision  data-t-* >
 *   [data-quiz-topbar] [data-quiz-progressbar] [data-quiz-indicator]
 *   [data-quiz-steps]
 *      <section data-step="welcome" data-quiz-welcome>
 *      <fieldset data-step="1" data-qid="q1" data-required="true|false">
 *         <label class="zqe-answer"
 *            data-infer-level | data-gf-adjustment | data-weight-midpoint |
 *            data-length-bias>  <input type="radio" name="q1"> …
 *      <section data-step="email" data-quiz-email-step>
 *         [data-quiz-firstname] [data-quiz-email] [data-quiz-email-status]
 *   [data-quiz-nav] [data-quiz-prev] [data-quiz-next]
 *   <template data-quiz-candidates>
 *      <article data-handle data-volume data-shape data-level data-price
 *               data-url data-variant-id>
 *         [data-quiz-board-tag] [data-spec-volume][data-spec-level]
 *         [data-quiz-board-explain] …card markup…
 *   [data-quiz-results] (hidden)
 *      [data-quiz-verdict-num] [data-quiz-results-heading]
 *      [data-quiz-results-list] [data-quiz-discount] [data-quiz-restart]
 * ════════════════════════════════════════════════════════════════════════════
 */
(function () {
  "use strict";

  var KLAVIYO_SUBSCRIBE = "https://a.klaviyo.com/client/subscriptions/";
  var KLAVIYO_EVENTS = "https://a.klaviyo.com/client/events/";
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Realistic GF = the skill's "Exemple 70kg" volume column (volume = weight × GF).
  // The skill's GF column (0.34–0.50) is HALF of what its own example volumes need
  // (70kg @1.3 → 63L ⇒ GF 0.90), so we use the example-consistent coefficients.
  // CONFIRM canonical numbers with Zeus before go-live (see handover flag).
  var GF_BASE = {
    1.1: 1.0,
    1.2: 0.94,
    1.3: 0.9,
    2.1: 0.8,
    2.2: 0.76,
    3.1: 0.72,
    3.2: 0.68,
  };
  var GF_UNKNOWN = 0.9; // level not in the table
  var DEFAULT_LEVEL = "1.2"; // no level answer → post-lesson beginner (NEVER intermediate)
  var DEFAULT_WEIGHT = 75; // weight missing (should not happen — required)
  var SAFETY_MARGIN = 0; // target = skill minimum volume; asymmetric band biases upward
  var GF_MIN = 0.55,
    GF_MAX = 1.1;

  // DEFAULT honest band: tolerate MORE volume than target, never strand below.
  var BAND_LOW = 6; // targetVol - 6
  var BAND_HIGH = 14; // targetVol + 14
  // ALTERNATIVE bands relative to the default board volume.
  var ALT_UP_MIN = 1,
    ALT_UP_MAX = 14;
  var ALT_DOWN_MIN = 1,
    ALT_DOWN_MAX = 12;
  // Cabianca Pro soft level gate: exclude when surfer level < this.
  var PRO_GATE_LEVEL = 2.2;
  // Level-suitability gate widens the board's stated level by ±this so a board
  // tagged with a single FFS value still matches an adjacent surfer level.
  var LEVEL_SLACK = 0.3;

  var AUTO_ADVANCE_MS = 280;
  var LOADING_MS = 420; // sits inside the 200–600ms "I'm thinking" window
  var RECO_STORAGE_KEY = "zeus-board-reco";
  var RECO_EVENT = "zeus:board-reco";

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? (fallback == null ? null : fallback) : n;
  }
  function round2(n) {
    return Math.round(n * 100) / 100;
  }
  function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
  }
  function levelNum(s) {
    var n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  }

  // Parse a board's stated level into a numeric [min, max] FFS range.
  // Accepts "1.1", "1.1-1.3", "1.1 – 1.3", or non-numeric ("Débutant" → null).
  // null/null means "level unknown" — such a board is never gated out.
  function parseLevelRange(s) {
    var str = String(s || "");
    var matches = str.match(/\d+(?:\.\d+)?/g);
    if (matches && matches.length) {
      var nums = matches.map(parseFloat).filter(function (n) {
        return !isNaN(n);
      });
      if (nums.length) return { min: Math.min.apply(null, nums), max: Math.max.apply(null, nums) };
    }
    // Live Zeus data stores skill_level as FRENCH LABELS, not FFS codes
    // (catalog audit 2026-06: "Débutant-e", "Confirmé-e", "Expert-e",
    // "Tous Niveaux", space/comma-joined). Map labels → FFS band so the
    // level-gate actually works on production data.
    var low = str.toLowerCase();
    var lo = null,
      hi = null;
    function span(a, b) {
      lo = lo == null ? a : Math.min(lo, a);
      hi = hi == null ? b : Math.max(hi, b);
    }
    if (/tous\s*niveaux/.test(low)) span(1.1, 3.2);
    if (/d[ée]butant/.test(low)) span(1.1, 1.3);
    if (/confirm/.test(low)) span(2.1, 2.2);
    if (/expert/.test(low)) span(3.1, 3.2);
    return { min: lo, max: hi };
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      return false;
    }
  }

  class ZeusQuizEngine extends HTMLElement {
    connectedCallback() {
      this.steps = Array.prototype.slice.call(this.querySelectorAll("[data-quiz-steps] > [data-step]"));
      if (!this.steps.length) return;

      this.total = this.steps.length; // includes welcome + email
      this.qTotal = num(this.dataset.questionTotal, 4); // questions only (for N/4 rail)
      this.answers = {}; // { q1: {ids:[], els:[]} }
      this.current = 0; // index into this.steps (0 = welcome)
      this.locale = this.dataset.locale || "fr";
      this.t = this.readStrings();
      this.emailRequired = this.dataset.emailRequired === "true";
      this.sessionId = this.makeSessionId();

      this.topbar = this.querySelector("[data-quiz-topbar]");
      this.progressBar = this.querySelector("[data-quiz-progressbar]");
      this.indicator = this.querySelector("[data-quiz-indicator]");
      this.navWrap = this.querySelector("[data-quiz-nav]");
      this.prevBtn = this.querySelector("[data-quiz-prev]");
      this.nextBtn = this.querySelector("[data-quiz-next]");
      this.resultsEl = this.querySelector("[data-quiz-results]");
      this.restartBtn = this.querySelector("[data-quiz-restart]");

      this.bindAnswers();
      this.bindNav();
      this.bindSwipe();
      this.bindUnload();
      this.initBentoModal();
      this.bindWelcomeActions();
      this.bindStickyCta();

      this.show(0);
      this.track("started", { step_index: 0 });
    }

    /* ── i18n ──────────────────────────────────────────────────────────── */
    readStrings() {
      var d = this.dataset;
      return {
        start: d.tStart || "Start the quiz",
        next: d.tNext || "Next",
        prev: d.tPrev || "Previous",
        skip: d.tSkip || "Skip",
        cont: d.tContinue || "Continue",
        finish: d.tFinish || "See my results",
        progress: d.tProgress || "__current__/__total__",
        selectError: d.tSelectError || "Pick an answer to continue.",
        emailRequiredError: d.tEmailRequiredError || "Email is required.",
        emailInvalidError: d.tEmailInvalidError || "Enter a valid email.",
        copied: d.tCopied || "Copied",
        atcSuccess: d.tAtcSuccess || "Added",
        loading: d.tLoading || "Finding your board…",
        errorGeneric: d.tErrorGeneric || "Something went wrong.",
        resultsHeading: d.tResultsHeading || "Here's what I'd put you on",
        resultsHeadingNamed: d.tResultsHeadingNamed || "__name__, here's what I'd put you on",
        verdictUnit: d.tVerdictUnit || "L",
        tagDefault: d.tTagDefault || "My pick",
        tagAlternative: d.tTagAlternative || "If you're unsure",
        specSeparator: d.tSpecSeparator || " · ",
        constructionSoft: d.tConstructionSoft || "Soft",
        viewBoard: d.tViewBoard || "View the board",
        viewBoardBento: d.tViewBoardBento || d.tViewBoard || "View the board",
        welcomeExplore: d.tWelcomeExplore || "Browse boards",
        expDefault: d.tExpDefault || "Your target volume: {volume} L. The {board} lands right on it.",
        expAltMore: d.tExpAltMore || "Go for the {altBoard}: a touch more volume.",
        expAltLess: d.tExpAltLess || "Switch to the {altBoard}: a little less volume.",
        expAltGeneric: d.tExpAltGeneric || "As a second option, the {altBoard}: {altReason}.",
        levelUnknownNote: d.tLevelUnknownNote || "",
      };
    }

    /* ── step navigation ───────────────────────────────────────────────── */
    bindNav() {
      var self = this;
      if (this.nextBtn)
        this.nextBtn.addEventListener("click", function () {
          self.next();
        });
      if (this.prevBtn)
        this.prevBtn.addEventListener("click", function () {
          self.prev();
        });
      if (this.restartBtn)
        this.restartBtn.addEventListener("click", function () {
          self.restart();
        });
      var skipEmail = this.querySelector("[data-quiz-email-skip]");
      if (skipEmail && !this.emailRequired)
        skipEmail.addEventListener("click", function () {
          self.submitEmailThenResults(true);
        });
    }

    bindWelcomeActions() {
      var self = this;
      var start = this.querySelector("[data-quiz-welcome-start]");
      if (start)
        start.addEventListener("click", function () {
          self.next();
        });

      var explore = this.querySelector("[data-quiz-welcome-explore]");
      if (explore) {
        explore.addEventListener("click", function (event) {
          var target =
            document.getElementById("board-selector-explore") ||
            document.querySelector(".shopify-section--zeus-board-selector") ||
            document.querySelector(".shopify-section--zeus-category-grid");
          if (!target) return;
          event.preventDefault();
          var headerOffset = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue("--header-height") || "72",
            10,
          );
          var top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
          window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        });
      }
    }

    initBentoModal() {
      if (this.dataset.bentoEnabled !== "true" || !window.ZeusBselModal) return;
      var root = this.querySelector("[data-bsel-modal-root]");
      if (!root) return;
      this.bselModal = new window.ZeusBselModal(root);
    }

    bindStickyCta() {
      var sticky = this.closest(".zqe-section")?.querySelector(".zqe-sticky-cta");
      if (!sticky) return;
      var self = this;
      var onScroll = function () {
        var rect = self.getBoundingClientRect();
        var past = rect.bottom < 0;
        var nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 120;
        sticky.hidden = !(past && !nearBottom);
        sticky.classList.toggle("is-visible", past && !nearBottom);
        if (window.ZeusStickyCtaCoordinator) window.ZeusStickyCtaCoordinator.sync();
      };
      this._stickyOnScroll = onScroll;
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    }

    bindSwipe() {
      var self = this;
      var zone = this.querySelector("[data-quiz-steps]");
      if (!zone || !window.matchMedia("(pointer: coarse)").matches) return;

      var startX = 0,
        startY = 0,
        startT = 0;

      zone.addEventListener(
        "touchstart",
        function (e) {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          startT = Date.now();
        },
        { passive: true },
      );

      zone.addEventListener(
        "touchend",
        function (e) {
          if (self._swipeLock) return;
          if (self.resultsEl && !self.resultsEl.hidden) return;
          var t = e.changedTouches[0];
          var dx = t.clientX - startX;
          var dy = t.clientY - startY;
          if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
          if (Date.now() - startT > 650) return;
          if (dx < 0) self.next();
          else self.prev();
        },
        { passive: true },
      );
    }

    bindAnswers() {
      var self = this;
      this.querySelectorAll("[data-quiz-steps] .zqe-answer input").forEach(function (input) {
        input.addEventListener("change", function () {
          var step = input.closest("[data-step]");
          self.recordAnswer(step);
          self.refreshNextState();
          // Auto-advance on single-select so the tap feels resolved.
          if (self.steps[self.current] === step) self.maybeAutoAdvance(step);
        });
      });
    }

    maybeAutoAdvance(step) {
      // Only on question steps (radio, single answer recorded).
      if (this.isWelcome(step) || this.isEmailStep(step)) return;
      if (!this.isStepAnswered(step)) return;
      var self = this;
      var go = function () {
        // Guard: user may have moved on already.
        if (self.steps[self.current] !== step) return;
        self.next();
      };
      if (prefersReducedMotion()) {
        go();
        return;
      }
      window.clearTimeout(this._advanceTimer);
      this._advanceTimer = window.setTimeout(go, AUTO_ADVANCE_MS);
    }

    recordAnswer(step) {
      var qid = step.dataset.qid;
      var checked = step.querySelectorAll(".zqe-answer input:checked");
      var ids = [];
      var els = [];
      step.querySelectorAll(".zqe-answer").forEach(function (l) {
        l.classList.remove("is-selected");
      });
      checked.forEach(function (input) {
        var label = input.closest(".zqe-answer");
        ids.push(input.value);
        els.push(label);
        label.classList.add("is-selected");
      });
      this.answers[qid] = { ids: ids, els: els };
      var err = step.querySelector("[data-quiz-step-error]");
      if (err) err.classList.remove("is-visible");
    }

    isWelcome(step) {
      return step && step.hasAttribute("data-quiz-welcome");
    }
    isEmailStep(step) {
      return step && step.hasAttribute("data-quiz-email-step");
    }

    isStepAnswered(step) {
      if (this.isWelcome(step)) return true;
      if (this.isEmailStep(step)) return this.emailValid();
      if (step.dataset.required === "false") return true;
      var a = this.answers[step.dataset.qid];
      return !!(a && a.ids.length);
    }

    // Position among the 4 question steps (1-based); 0 = not a question step.
    questionPos(index) {
      var step = this.steps[index];
      if (!step) return 0;
      var n = num(step.dataset.step, 0);
      return n >= 1 && n <= this.qTotal ? n : 0;
    }

    show(index) {
      var self = this;
      window.clearTimeout(this._advanceTimer);
      var prevIndex = this.current;
      var goingBack = index < prevIndex;
      this.steps.forEach(function (s, i) {
        s.classList.remove("is-back");
        if (i === index) {
          s.classList.add("is-active");
          if (goingBack) s.classList.add("is-back");
        } else {
          s.classList.remove("is-active");
        }
      });
      this.current = index;
      var step = this.steps[index];
      var welcome = this.isWelcome(step);
      var email = this.isEmailStep(step);
      var qPos = this.questionPos(index);

      // Top bar / progress: visible only on question + email steps.
      if (this.topbar) this.topbar.hidden = welcome;
      var fillPct = welcome ? 0 : ((email ? this.qTotal : qPos) / this.qTotal) * 100;
      if (this.progressBar) this.progressBar.style.width = fillPct + "%";
      if (this.indicator) {
        this.indicator.textContent = welcome
          ? ""
          : this.t.progress.replace("__current__", email ? this.qTotal : qPos).replace("__total__", this.qTotal);
      }

      // Back chevron: hidden on welcome (and on first question there is a Back to welcome).
      if (this.prevBtn) this.prevBtn.hidden = welcome;

      // Bottom nav: hidden on welcome (dual CTA in panel) and on results.
      if (this.navWrap) {
        this.navWrap.hidden = welcome || !!(this.resultsEl && !this.resultsEl.hidden);
      }

      // Primary CTA label + enabled state.
      if (this.nextBtn) {
        var label = this.t.next;
        if (welcome) label = this.t.start;
        else if (email) label = this.t.finish;
        else if (qPos === this.qTotal) label = this.t.cont;
        else if (step.dataset.required === "false" && !this.isStepAnswered(step)) label = this.t.skip;
        this.nextBtn.textContent = label;
      }
      this.refreshNextState();

      // Brief lock so swipe doesn't double-fire during the slide beat.
      if (!prefersReducedMotion() && index !== prevIndex) {
        this._swipeLock = true;
        window.clearTimeout(this._swipeTimer);
        this._swipeTimer = window.setTimeout(function () {
          self._swipeLock = false;
        }, 360);
      }

      // a11y: move focus to the step heading (preventScroll so step-to-step
      // navigation never yanks the whole window back to the top).
      var heading = step.querySelector("legend, [data-quiz-email-heading], h1, h2, .zqe-eyebrow");
      if (heading) {
        heading.setAttribute("tabindex", "-1");
        try {
          heading.focus({ preventScroll: true });
        } catch (e) {
          heading.focus();
        }
      }
    }

    refreshNextState() {
      if (!this.nextBtn) return;
      var step = this.steps[this.current];
      // Email step: CTA always clickable — it validates the email on click and
      // shows an inline error (no input listener needed, matches the mockup).
      // Welcome + optional question steps: enabled (optional acts as "Skip").
      // Required question steps: gated until an answer is recorded.
      var enabled =
        this.isWelcome(step) ||
        this.isEmailStep(step) ||
        step.dataset.required === "false" ||
        this.isStepAnswered(step);
      this.nextBtn.disabled = !enabled;
      // Relabel skip→next when an optional step gets an answer.
      if (step && step.dataset.required === "false" && !this.isWelcome(step) && !this.isEmailStep(step)) {
        this.nextBtn.textContent = this.isStepAnswered(step)
          ? this.questionPos(this.current) === this.qTotal
            ? this.t.cont
            : this.t.next
          : this.t.skip;
      }
    }

    next() {
      var step = this.steps[this.current];

      // Email step owns its own validation (empty vs invalid messaging).
      if (this.isEmailStep(step)) {
        this.submitEmailThenResults();
        return;
      }

      if (!this.isStepAnswered(step) && step.dataset.required !== "false" && !this.isWelcome(step)) {
        this.flashError();
        return;
      }

      if (!this.isWelcome(step)) {
        this.track("step", {
          step_index: this.questionPos(this.current),
          answers: this.collectAnswerIds(),
        });
      }

      if (this.current < this.total - 1) {
        this.show(this.current + 1);
      } else {
        this.finish(); // no email step configured
      }
    }

    prev() {
      if (this.current > 0) this.show(this.current - 1);
    }

    flashError() {
      var step = this.steps[this.current];
      var err = step.querySelector("[data-quiz-step-error]");
      if (err) {
        err.textContent = this.t.selectError;
        err.classList.add("is-visible");
      }
    }

    /* ── email gate + Klaviyo ──────────────────────────────────────────── */
    emailValue() {
      var el = this.querySelector("[data-quiz-email]");
      return el ? (el.value || "").trim() : "";
    }
    firstNameValue() {
      var el = this.querySelector("[data-quiz-firstname]");
      return el ? (el.value || "").trim() : "";
    }
    emailValid() {
      return EMAIL_RE.test(this.emailValue());
    }

    setEmailStatus(msg, type) {
      var el = this.querySelector("[data-quiz-email-status]");
      if (!el) return;
      el.textContent = msg || "";
      el.classList.remove("is-error", "is-success");
      if (type) el.classList.add("is-" + type);
    }

    submitEmailThenResults(skipEmail) {
      if (this.emailSubmitting || (skipEmail && this.emailRequired)) return;
      if (skipEmail) {
        // Explicitly declining email also discards an address already entered.
        var skippedInput = this.querySelector("[data-quiz-email]");
        if (skippedInput) skippedInput.value = "";
      }
      if ((this.emailRequired || this.emailValue()) && !this.emailValid()) {
        // Empty → "required"; non-empty but malformed → "invalid".
        var msg = this.emailValue() ? this.t.emailInvalidError : this.t.emailRequiredError;
        this.setEmailStatus(msg, "error");
        var el = this.querySelector("[data-quiz-email]");
        if (el)
          try {
            el.focus();
          } catch (e) {}
        return;
      }
      this.emailSubmitting = true;
      this.setEmailStatus("", null);
      // Marketing side is fire-and-forget: never block the user from results.
      if (!skipEmail && this.emailValid()) this.subscribeToKlaviyo();
      this.track("email", { step_index: this.qTotal, email_given: this.emailValid() });

      // LOADING micro-state: prevent double-submit, sell the "I'm thinking" beat.
      var self = this;
      if (this.nextBtn) {
        this.nextBtn.disabled = true;
        this.nextBtn.classList.add("is-loading");
        this.nextBtn.textContent = this.t.loading;
      }
      if (this.topbar) this.topbar.classList.add("is-pulsing");
      var delay = prefersReducedMotion() ? 0 : LOADING_MS;
      window.setTimeout(function () {
        if (self.topbar) self.topbar.classList.remove("is-pulsing");
        if (self.nextBtn) self.nextBtn.classList.remove("is-loading");
        self.finish();
      }, delay);
    }

    subscribeToKlaviyo() {
      var companyId = this.dataset.companyId;
      var listId = this.dataset.listId;
      var revision = this.dataset.klaviyoRevision || "2024-10-15";
      if (!companyId || !listId) {
        console.warn("[zeus-quiz] missing Klaviyo company_id or list_id; subscribe skipped");
        return;
      }
      var email = this.emailValue();
      var props = this.profileProperties();

      // client/subscriptions schema mirrors the working BIS call; revision pins the contract.
      var subPayload = {
        data: {
          type: "subscription",
          attributes: {
            custom_source: "quiz-board-finder",
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                  first_name: this.firstNameValue() || undefined,
                  properties: props,
                },
              },
            },
          },
          relationships: { list: { data: { type: "list", id: listId } } },
        },
      };
      this.klaviyoPost(KLAVIYO_SUBSCRIBE, companyId, revision, subPayload);

      // Rich Track event for segmentation/flows (list subscribe alone is weak).
      var evtPayload = {
        data: {
          type: "event",
          attributes: {
            metric: { data: { type: "metric", attributes: { name: "Completed Board Quiz" } } },
            properties: props,
            profile: { data: { type: "profile", attributes: { email: email } } },
          },
        },
      };
      this.klaviyoPost(KLAVIYO_EVENTS, companyId, revision, evtPayload);
    }

    klaviyoPost(url, companyId, revision, payload) {
      fetch(url + "?company_id=" + encodeURIComponent(companyId), {
        method: "POST",
        headers: { "Content-Type": "application/json", revision: revision },
        body: JSON.stringify(payload),
      }).catch(function () {
        /* fire-and-forget */
      });
    }

    profileProperties() {
      var m = this.match || {};
      return {
        zeus_quiz_locale: this.locale,
        zeus_quiz_ffs_level: m.level || "",
        zeus_quiz_guild_factor: m.gf || "",
        zeus_quiz_target_volume: m.targetVol || "",
        zeus_quiz_recommended: (m.handles || []).join(","),
        zeus_quiz_answers: JSON.stringify(this.collectAnswerIds()),
      };
    }

    /* ── matching (Guild-Factor reco, skill §4) ────────────────────────── */
    computeMatch() {
      var conditionsAdj = 0,
        lengthBias = 0,
        weight = null,
        level = null;
      var lowConfidenceWeight = false;

      this.steps.forEach((step) => {
        if (this.isWelcome(step) || this.isEmailStep(step)) return;
        var a = this.answers[step.dataset.qid];
        if (!a) return;
        a.els.forEach(function (el) {
          var d = el.dataset;
          conditionsAdj += num(d.gfAdjustment, 0);
          lengthBias += num(d.lengthBias, 0);
          if (d.weightMidpoint) weight = num(d.weightMidpoint, weight);
          if (d.inferLevel) level = d.inferLevel;
        });
      });

      var levelKnown = !!level;
      if (!level) level = DEFAULT_LEVEL;
      if (weight == null) {
        weight = DEFAULT_WEIGHT;
        lowConfidenceWeight = true;
      }

      var gfBase = GF_BASE[level] || GF_UNKNOWN;
      var gf = clamp(round2(gfBase + conditionsAdj), GF_MIN, GF_MAX);
      var targetVol = Math.round(weight * gf) + SAFETY_MARGIN; // SAFETY_MARGIN=0: target = skill minimum

      this.match = {
        gf: gf,
        level: level,
        levelKnown: levelKnown,
        weight: weight,
        lowConfidenceWeight: lowConfidenceWeight,
        targetVol: targetVol,
        lengthBias: lengthBias,
        handles: [],
      };
      return this.match;
    }

    candidates() {
      var tpl = this.querySelector("[data-quiz-candidates]");
      if (!tpl) return [];
      var nodes = tpl.content ? tpl.content.querySelectorAll("[data-handle]") : tpl.querySelectorAll("[data-handle]");
      return (
        Array.prototype.slice
          .call(nodes)
          .map(function (node) {
            var levelStr = node.dataset.level || "";
            var range = parseLevelRange(levelStr);
            return {
              node: node.cloneNode(true),
              handle: node.dataset.handle,
              volume: num(node.dataset.volume, null),
              shape: node.dataset.shape || "",
              level: levelStr,
              levelMin: range.min,
              levelMax: range.max,
              price: num(node.dataset.price, null), // cents
              name:
                (node.querySelector(".zeus-hpg-card__title, .zqe-board__name") || {}).textContent ||
                node.dataset.handle,
            };
          })
          // Drop cards with no numeric volume — cannot be ranked honestly.
          .filter(function (c) {
            return c.volume != null;
          })
      );
    }

    // Deterministic sort by volume distance to a target, then lengthBias, price, handle.
    sortByDistance(pool, target, lengthBias) {
      return pool.slice().sort(function (a, b) {
        var da = Math.abs(a.volume - target),
          db = Math.abs(b.volume - target);
        if (da !== db) return da - db;
        if (lengthBias) {
          var byVol = lengthBias > 0 ? b.volume - a.volume : a.volume - b.volume;
          if (byVol !== 0) return byVol;
        }
        var pa = a.price == null ? Infinity : a.price;
        var pb = b.price == null ? Infinity : b.price;
        if (pa !== pb) return pa - pb;
        return a.handle < b.handle ? -1 : a.handle > b.handle ? 1 : 0;
      });
    }

    pickDefault(pool, m) {
      // Honest band: tolerate MORE volume, never strand below the safe minimum.
      var lo = m.targetVol - BAND_LOW,
        hi = m.targetVol + BAND_HIGH;
      var inBand = pool.filter(function (c) {
        return c.volume >= lo && c.volume <= hi;
      });
      // Empty band → prefer at/above target so the fallback never strands below
      // the safe minimum; only if nothing sits at/above target do we drop back to
      // the full pool (original abs-distance behaviour).
      var atOrAbove = pool.filter(function (c) {
        return c.volume >= m.targetVol;
      });
      var fallbackPool = atOrAbove.length ? atOrAbove : pool;
      var ranked = this.sortByDistance(inBand.length ? inBand : fallbackPool, m.targetVol, m.lengthBias);
      return ranked[0] || null;
    }

    pickAlternative(pool, def, m) {
      if (!def) return null;
      var others = pool.filter(function (c) {
        return c.handle !== def.handle;
      });
      if (!others.length) return null;
      var dv = def.volume;

      // 1) nearest candidate ABOVE default by +1..+14L → more forgiving small days.
      var up = others
        .filter(function (c) {
          return c.volume - dv >= ALT_UP_MIN && c.volume - dv <= ALT_UP_MAX;
        })
        .sort(function (a, b) {
          return a.volume - dv - (b.volume - dv);
        });
      if (up.length) return { board: up[0], direction: "more_volume" };

      // 2) else nearest BELOW default by -1..-12L → playful / steeper days.
      var down = others
        .filter(function (c) {
          return dv - c.volume >= ALT_DOWN_MIN && dv - c.volume <= ALT_DOWN_MAX;
        })
        .sort(function (a, b) {
          return dv - a.volume - (dv - b.volume);
        });
      if (down.length) return { board: down[0], direction: "less_volume" };

      // 3) else next-closest distinct by abs-distance to targetVol.
      var nearest = this.sortByDistance(others, m.targetVol, m.lengthBias)[0];
      if (!nearest) return null;
      return { board: nearest, direction: nearest.volume >= dv ? "more_volume" : "less_volume" };
    }

    levelGate(pool, m) {
      // Level-suitability gate: keep boards whose stated FFS range overlaps the
      // surfer's level (±slack), so a beginner never gets handed a performance
      // fish and an expert never gets a foam mini-malibu. This is the honest
      // generalisation of the spec's Pro-shape gate. Boards with unknown level
      // (no numeric data-level) are kept — we cannot honestly exclude them.
      var surfer = levelNum(m.level);
      var suited = pool.filter(function (c) {
        if (c.levelMin == null) return true; // level unknown → keep
        return c.levelMin - LEVEL_SLACK <= surfer && surfer <= c.levelMax + LEVEL_SLACK;
      });
      if (suited.length) return suited;

      // Fallback A: at least drop Cabianca Pro shapes for sub-2.2 surfers.
      if (surfer < PRO_GATE_LEVEL) {
        var noPro = pool.filter(function (c) {
          return c.levelMin == null || c.levelMin < PRO_GATE_LEVEL;
        });
        if (noPro.length) return noPro;
      }
      // Fallback B: never return zero boards.
      return pool;
    }

    rankResults() {
      var m = this.computeMatch();
      var pool = this.levelGate(this.candidates(), m);
      if (!pool.length) {
        m.handles = [];
        return [];
      }

      var def = this.pickDefault(pool, m);
      var altPick = this.pickAlternative(pool, def, m);
      var out = [];
      if (def) out.push({ board: def, role: "default", direction: null });
      if (altPick) out.push({ board: altPick.board, role: "alternative", direction: altPick.direction });

      m.handles = out.map(function (o) {
        return o.board.handle;
      });
      m.altDirection = altPick ? altPick.direction : null;
      return out;
    }

    /* ── results render ────────────────────────────────────────────────── */
    finish() {
      var picks = this.rankResults();
      this.renderVerdict();
      this.renderResults(picks);
      this.renderDiscount();
      this.applyDiscount();
      this.renderResultsHeading();
      this.bindResultsActions();
      this.publishReco();
      this.revealCarouselCta();

      this.steps.forEach(function (s) {
        s.classList.remove("is-active");
      });
      if (this.topbar) this.topbar.hidden = true;
      if (this.navWrap) this.navWrap.hidden = true;
      if (this.resultsEl) {
        this.resultsEl.hidden = false;
        this.resultsEl.classList.add("is-active");
        var h = this.resultsEl.querySelector("[data-quiz-results-heading], h2");
        if (h) {
          h.setAttribute("tabindex", "-1");
          try {
            h.focus({ preventScroll: true });
          } catch (e) {
            h.focus();
          }
        }
      }
      if (this.resultsEl) {
        try {
          this.resultsEl.scrollIntoView({
            behavior: prefersReducedMotion() ? "auto" : "smooth",
            block: "start",
          });
        } catch (e) {
          this.resultsEl.scrollIntoView();
        }
      }

      this.track("completed", {
        step_index: this.qTotal,
        answers: this.collectAnswerIds(),
        completion_state: "completed",
        email_given: this.emailValid(),
        discount_shown: !!this.dataset.discountCode,
      });
    }

    publishReco() {
      var m = this.match;
      if (!m || !m.handles || !m.handles.length) return;
      var payload = {
        handles: m.handles.slice(0, 2),
        targetVol: m.targetVol,
        level: m.level,
        gf: m.gf,
        ts: Date.now(),
      };
      try {
        sessionStorage.setItem(RECO_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {}
      try {
        document.dispatchEvent(new CustomEvent(RECO_EVENT, { detail: payload }));
      } catch (e2) {}
    }

    revealCarouselCta() {
      var cta = this.querySelector("[data-quiz-carousel-explore]");
      if (cta) cta.hidden = false;
    }

    renderVerdict() {
      var el = this.querySelector("[data-quiz-verdict-num]");
      var m = this.match || {};
      if (el && m.targetVol != null) el.textContent = m.targetVol + " " + this.t.verdictUnit;
    }

    renderResultsHeading() {
      var el = this.querySelector("[data-quiz-results-heading]");
      if (!el) return;
      var name = this.firstNameValue();
      el.textContent = name ? this.t.resultsHeadingNamed.replace("__name__", name) : this.t.resultsHeading;
    }

    fillTemplate(tpl, vars) {
      return (tpl || "").replace(/\{(\w+)\}/g, function (_, k) {
        return vars[k] != null ? vars[k] : "";
      });
    }

    decorateCard(pick) {
      var node = pick.board.node;
      var m = this.match || {};
      var name = pick.board.name;
      var isDefault = pick.role === "default";

      node.classList.add(isDefault ? "is-default" : "is-alternative");

      // Tag — "Mon choix" / "Si tu hésites".
      var tag = node.querySelector("[data-quiz-board-tag]");
      if (tag) {
        tag.textContent = isDefault ? this.t.tagDefault : this.t.tagAlternative;
        tag.hidden = false;
      }

      // Explanation line.
      var explain = node.querySelector("[data-quiz-board-explain]");
      if (explain) {
        var text;
        if (isDefault) {
          text = this.fillTemplate(this.t.expDefault, {
            volume: m.targetVol,
            weight: m.weight,
            level: m.level,
            board: name,
          });
        } else {
          if (pick.direction === "more_volume") {
            text = this.fillTemplate(this.t.expAltMore, { altBoard: name });
          } else if (pick.direction === "less_volume") {
            text = this.fillTemplate(this.t.expAltLess, { altBoard: name });
          } else {
            text = this.fillTemplate(this.t.expAltGeneric, { altBoard: name, altReason: "" });
          }
        }
        explain.textContent = text;
      }

      // CTA label.
      var cta = node.querySelector("[data-quiz-bsel-open], .zqe-board__cta");
      if (cta) {
        cta.textContent = this.dataset.bentoEnabled === "true" ? this.t.viewBoardBento : this.t.viewBoard;
      }

      return node;
    }

    renderResults(picks) {
      var list = this.querySelector("[data-quiz-results-list]");
      if (!list) return;
      list.innerHTML = "";
      if (!picks.length) {
        list.textContent = this.t.errorGeneric;
        return;
      }
      var self = this;
      picks.forEach(function (pick) {
        list.appendChild(self.decorateCard(pick));
      });

      // Level-unknown note: surfer never told us their level → soft disclaimer.
      var m = this.match || {};
      if (!m.levelKnown && this.t.levelUnknownNote) {
        var note = document.createElement("p");
        note.className = "zqe-results__note";
        note.textContent = this.t.levelUnknownNote;
        list.appendChild(note);
      }
    }

    renderDiscount() {
      var box = this.querySelector("[data-quiz-discount]");
      if (!box) return;
      box.hidden = !this.dataset.discountCode;
    }

    // CRO: pre-apply the discount to the session the moment results show, so it
    // carries to cart + checkout with zero clicks. Gated by the theme-editor
    // toggle (data-auto-apply-discount). The visible code + "apply" link stay as
    // a fallback. Shopify's /discount/CODE sets the session discount cookie; we
    // follow the redirect with a same-origin fetch so the page never navigates.
    applyDiscount() {
      if (this.dataset.autoApplyDiscount !== "true") return;
      var url = this.dataset.discountUrl;
      if (!url) return;
      try {
        fetch(url, { credentials: "same-origin" })
          .then(function () {})
          .catch(function () {});
      } catch (e) {}
      var box = this.querySelector("[data-quiz-discount]");
      if (box) box.classList.add("is-applied");
    }

    bindResultsActions() {
      var self = this;
      // copy-to-clipboard for the discount code
      var copyBtn = this.querySelector("[data-quiz-copy]");
      if (copyBtn && !copyBtn._bound) {
        copyBtn._bound = true;
        copyBtn.addEventListener("click", function () {
          var code = self.dataset.discountCode || "";
          var orig = copyBtn.getAttribute("data-label") || copyBtn.textContent;
          var done = function () {
            copyBtn.textContent = self.t.copied;
            setTimeout(function () {
              copyBtn.textContent = orig;
            }, 2000);
          };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(done).catch(done);
          } else {
            var ta = document.createElement("textarea");
            ta.value = code;
            document.body.appendChild(ta);
            ta.select();
            try {
              document.execCommand("copy");
            } catch (e) {}
            document.body.removeChild(ta);
            done();
          }
        });
      }
      // result card click → analytics + bento modal
      if (this.resultsEl && !this.resultsEl._boundClicks) {
        this.resultsEl._boundClicks = true;
        this.resultsEl.addEventListener("click", function (e) {
          var bentoBtn = e.target.closest("[data-quiz-bsel-open]");
          if (bentoBtn) {
            e.preventDefault();
            var card = bentoBtn.closest("[data-handle]");
            var url = card && card.dataset.url;
            var handle = card && card.dataset.handle;
            if (self.bselModal && url) {
              self.bselModal.openProduct(url, handle);
              self.track("result_bento_open", { answers: self.collectAnswerIds(), handle: handle || "" });
            }
            return;
          }
          var cta = e.target.closest(".zqe-board__cta, .zeus-hpg-card__title");
          if (!cta) return;
          self.track("result_click", { answers: self.collectAnswerIds() });
        });
      }
    }

    restart() {
      this.emailSubmitting = false;
      this.answers = {};
      this.match = null;
      try {
        sessionStorage.removeItem(RECO_STORAGE_KEY);
      } catch (e) {}
      document.dispatchEvent(new CustomEvent(RECO_EVENT, { detail: { handles: [], targetVol: null } }));
      window.clearTimeout(this._advanceTimer);
      this.querySelectorAll(".zqe-answer.is-selected").forEach(function (l) {
        l.classList.remove("is-selected");
      });
      this.querySelectorAll("[data-quiz-steps] input").forEach(function (i) {
        i.checked = false;
      });
      var email = this.querySelector("[data-quiz-email]");
      var fn = this.querySelector("[data-quiz-firstname]");
      if (email) email.value = "";
      if (fn) fn.value = "";
      this.setEmailStatus("", null);
      if (this.resultsEl) {
        this.resultsEl.hidden = true;
        this.resultsEl.classList.remove("is-active");
      }
      var carouselCta = this.querySelector("[data-quiz-carousel-explore]");
      if (carouselCta) carouselCta.hidden = true;
      if (this.navWrap) this.navWrap.hidden = false;
      if (this.nextBtn) {
        this.nextBtn.disabled = false;
        this.nextBtn.classList.remove("is-loading");
      }
      this.sessionId = this.makeSessionId();
      this.show(0);
      this.track("started", { step_index: 0 });
    }

    /* ── analytics funnel (Apps Script / Sheet) ────────────────────────── */
    makeSessionId() {
      try {
        if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
      } catch (e) {}
      return "s-" + Date.now().toString(36) + "-" + Math.round(performance.now()).toString(36);
    }

    collectAnswerIds() {
      var out = {};
      for (var qid in this.answers) {
        if (this.answers.hasOwnProperty(qid)) out[qid] = this.answers[qid].ids.join("|");
      }
      return out;
    }

    track(eventType, extra) {
      var url = this.dataset.analyticsUrl;
      if (!url) return;
      var m = this.match || {};
      var payload = Object.assign(
        {
          source: "zeus-quiz",
          session_id: this.sessionId,
          event_type: eventType,
          locale: this.locale,
          ffs_level: m.level || "",
          guild_factor: m.gf || "",
          target_volume: m.targetVol || "",
          recommended_handles: m.handles || [],
          device: window.matchMedia && window.matchMedia("(max-width: 699px)").matches ? "mobile" : "desktop",
          referrer: document.referrer || "",
          page_url: location.href,
          user_agent: navigator.userAgent || "",
        },
        extra || {},
      );
      var body = JSON.stringify(payload);
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }));
          return;
        }
      } catch (e) {}
      fetch(url, { method: "POST", body: body, keepalive: true, mode: "no-cors" }).catch(function () {});
    }

    bindUnload() {
      var self = this;
      var fired = false;
      var onLeave = function () {
        if (fired) return;
        if (self.resultsEl && !self.resultsEl.hidden) return; // completed, not abandoned
        if (self.isWelcome(self.steps[self.current])) return; // never entered the funnel
        fired = true;
        self.track("abandoned", {
          step_index: self.questionPos(self.current) || (self.isEmailStep(self.steps[self.current]) ? self.qTotal : 0),
          completion_state: "abandoned",
          answers: self.collectAnswerIds(),
        });
      };
      var onVisibility = function () {
        if (document.visibilityState === "hidden") onLeave();
      };
      this._quizOnLeave = onLeave;
      this._quizOnVisibility = onVisibility;
      window.addEventListener("pagehide", onLeave);
      document.addEventListener("visibilitychange", onVisibility);
    }

    disconnectedCallback() {
      if (this._quizOnLeave) {
        window.removeEventListener("pagehide", this._quizOnLeave);
      }
      if (this._quizOnVisibility) {
        document.removeEventListener("visibilitychange", this._quizOnVisibility);
      }
      if (this._stickyOnScroll) {
        window.removeEventListener("scroll", this._stickyOnScroll);
      }
      if (this.bselModal) {
        this.bselModal.destroy();
        this.bselModal = null;
      }
    }
  }

  if (!customElements.get("zeus-quiz-engine")) {
    customElements.define("zeus-quiz-engine", ZeusQuizEngine);
  }
})();
